import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import dynamic from "next/dynamic";
import UsaFlag from "@components/insureyourcar/icons/useFlag";
import Header from "@components/shared/header";
import { useModal } from "@hooks/useModalContext";

import ListingModal from "@components/insureyourcar/listingModal";
import { getCookie, setCookie } from "cookies-next";
import { useDomainContext } from "@hooks/useDomainContext";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";

const Footer = dynamic(() => import("@components/shared/footer"));
const Modal = dynamic(() => import("@components/shared/modal"));
const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);
const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);

export default function InsureYourCarWrapper({
    children,
    headerMessage,
    state,
}: {
    headerMessage?: string | null | undefined;
    children: ReactNode;
    state?: string | null | undefined;
}): ReactElement {
    const { modal, setModal, closeModal } = useModal();

    const { clicksOffers: offers, visitDetails } = useDomainContext();

    const handleClose = () => {
        closeModal();
    };

    const { active: modalActive, content: modalContent } = modal;

    const handleMouseLeave = () => {
        if (
            document.activeElement?.tagName === "INPUT" ||
            document.activeElement?.tagName === "SELECT" ||
            !offers?.length ||
            (visitDetails?.trafficSourceType !== "Social Media" &&
                visitDetails?.trafficSourceNetwork !== "Propel Media")
        )
            return;
        if (!getCookie("listing-modal-shown")) {
            setModal({ active: true, content: "listingPage" });
            setCookie("listing-modal-shown", "true");
        }
    };

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return <PrivacyPolicy />;
            case "terms":
                return <TermsAndConditions />;
            case "listingPage":
                return offers && offers[0] ? (
                    <ListingModal item={offers[0]} state={state} />
                ) : null;
            case "sideWidget":
                return <Mobile />;
            default:
                return null;
        }
    };

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap"
                    rel="stylesheet"
                />
            </Head>

            <div
                style={{ fontFamily: `'Lato', sans-serif` }}
                onMouseLeave={handleMouseLeave}
            >
                <Header
                    slogan={headerMessage ?? "Top Auto Insurance Companies"}
                    emptyIcon={<UsaFlag />}
                />
                <main>{children}</main>
                <Footer bgColor="#1b447f" textColor="#fff" />
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalClassName={
                        modalContent === "listingPage"
                            ? undefined
                            : "h-[90vh] rounded"
                    }
                    modalStyles={
                        modalContent === "listingPage"
                            ? {
                                  display: "flex",
                                  flexDirection: "column",
                                  padding: "30px 20px",
                                  paddingBottom: "5px",
                                  fontFamily: `'Poppins', sans-serif`,
                                  borderRadius: "10px",
                              }
                            : {
                                  display: "flex",
                                  flexDirection: "column",
                                  maxHeight:
                                      modalContent === "sideWidget"
                                          ? "80vh"
                                          : "100%",
                              }
                    }
                    bodyStyles={{ overflowY: "scroll" }}
                    width={modalContent === "listingPage" ? 500 : 900}
                    header={
                        modalContent === "privacy"
                            ? "Privacy Policy"
                            : modalContent === "terms"
                            ? "Terms and Conditions"
                            : modalContent === "sideWidget"
                            ? "Contents"
                            : undefined
                    }
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
